/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Reelplicate API
 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  ApiErrorDto,
  CreateImageUploadDto,
  CreateWorkspaceDto,
  ImageUploadDto,
  SetImageDto,
  UpdateWorkspaceDto,
  WorkspaceDto,
} from '../../model';

import { customInstance } from '../../../mutator';
import type { ErrorType, BodyType } from '../../../mutator';

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary List the caller's workspaces, newest first.
 */
export const listWorkspaces = (signal?: AbortSignal) => {
  return customInstance<WorkspaceDto[]>({
    url: `/api/v1/workspaces`,
    method: 'GET',
    signal,
  });
};

export const getListWorkspacesQueryKey = () => {
  return [`/api/v1/workspaces`] as const;
};

export const getListWorkspacesQueryOptions = <
  TData = Awaited<ReturnType<typeof listWorkspaces>>,
  TError = ErrorType<ApiErrorDto>,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof listWorkspaces>>, TError, TData>
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListWorkspacesQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listWorkspaces>>> = ({
    signal,
  }) => listWorkspaces(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listWorkspaces>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListWorkspacesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listWorkspaces>>
>;
export type ListWorkspacesQueryError = ErrorType<ApiErrorDto>;

export function useListWorkspaces<
  TData = Awaited<ReturnType<typeof listWorkspaces>>,
  TError = ErrorType<ApiErrorDto>,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listWorkspaces>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWorkspaces>>,
          TError,
          Awaited<ReturnType<typeof listWorkspaces>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListWorkspaces<
  TData = Awaited<ReturnType<typeof listWorkspaces>>,
  TError = ErrorType<ApiErrorDto>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listWorkspaces>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWorkspaces>>,
          TError,
          Awaited<ReturnType<typeof listWorkspaces>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListWorkspaces<
  TData = Awaited<ReturnType<typeof listWorkspaces>>,
  TError = ErrorType<ApiErrorDto>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listWorkspaces>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List the caller's workspaces, newest first.
 */

export function useListWorkspaces<
  TData = Awaited<ReturnType<typeof listWorkspaces>>,
  TError = ErrorType<ApiErrorDto>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listWorkspaces>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListWorkspacesQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create a workspace owned by the caller.
 */
export const createWorkspace = (
  createWorkspaceDto: BodyType<CreateWorkspaceDto>,
  signal?: AbortSignal,
) => {
  return customInstance<WorkspaceDto>({
    url: `/api/v1/workspaces`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createWorkspaceDto,
    signal,
  });
};

export const getCreateWorkspaceMutationKey = () => ['createWorkspace'] as const;

export const getCreateWorkspaceMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createWorkspace>>,
    TError,
    CreateWorkspaceMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createWorkspace>>,
  TError,
  CreateWorkspaceMutationVariables,
  TContext
> => {
  const mutationKey = getCreateWorkspaceMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createWorkspace>>,
    CreateWorkspaceMutationVariables
  > = (props) => {
    const { data } = props ?? {};

    return createWorkspace(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateWorkspaceMutationResult = NonNullable<
  Awaited<ReturnType<typeof createWorkspace>>
>;
export type CreateWorkspaceMutationBody = BodyType<CreateWorkspaceDto>;
export type CreateWorkspaceMutationError = ErrorType<ApiErrorDto>;
export type CreateWorkspaceMutationVariables = { data: BodyType<CreateWorkspaceDto> };

/**
 * @summary Create a workspace owned by the caller.
 */
export const useCreateWorkspace = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createWorkspace>>,
      TError,
      CreateWorkspaceMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createWorkspace>>,
  TError,
  CreateWorkspaceMutationVariables,
  TContext
> => {
  return useMutation(getCreateWorkspaceMutationOptions(options), queryClient);
};
/**
 * @summary Get one workspace.
 */
export const getWorkspace = (id: string, signal?: AbortSignal) => {
  return customInstance<WorkspaceDto>({
    url: `/api/v1/workspaces/${id}`,
    method: 'GET',
    signal,
  });
};

export const getGetWorkspaceQueryKey = (id: string) => {
  return [`/api/v1/workspaces/${id}`] as const;
};

export const getGetWorkspaceQueryOptions = <
  TData = Awaited<ReturnType<typeof getWorkspace>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getWorkspace>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetWorkspaceQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getWorkspace>>> = ({ signal }) =>
    getWorkspace(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getWorkspace>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetWorkspaceQueryResult = NonNullable<
  Awaited<ReturnType<typeof getWorkspace>>
>;
export type GetWorkspaceQueryError = ErrorType<ApiErrorDto>;

export function useGetWorkspace<
  TData = Awaited<ReturnType<typeof getWorkspace>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getWorkspace>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkspace>>,
          TError,
          Awaited<ReturnType<typeof getWorkspace>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetWorkspace<
  TData = Awaited<ReturnType<typeof getWorkspace>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getWorkspace>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkspace>>,
          TError,
          Awaited<ReturnType<typeof getWorkspace>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetWorkspace<
  TData = Awaited<ReturnType<typeof getWorkspace>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getWorkspace>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get one workspace.
 */

export function useGetWorkspace<
  TData = Awaited<ReturnType<typeof getWorkspace>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getWorkspace>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetWorkspaceQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update a workspace: name, website, onboarding answers and its General settings.
 */
export const updateWorkspace = (
  id: string,
  updateWorkspaceDto: BodyType<UpdateWorkspaceDto>,
  signal?: AbortSignal,
) => {
  return customInstance<WorkspaceDto>({
    url: `/api/v1/workspaces/${id}`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: updateWorkspaceDto,
    signal,
  });
};

export const getUpdateWorkspaceMutationKey = () => ['updateWorkspace'] as const;

export const getUpdateWorkspaceMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateWorkspace>>,
    TError,
    UpdateWorkspaceMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateWorkspace>>,
  TError,
  UpdateWorkspaceMutationVariables,
  TContext
> => {
  const mutationKey = getUpdateWorkspaceMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateWorkspace>>,
    UpdateWorkspaceMutationVariables
  > = (props) => {
    const { id, data } = props ?? {};

    return updateWorkspace(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateWorkspaceMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateWorkspace>>
>;
export type UpdateWorkspaceMutationBody = BodyType<UpdateWorkspaceDto>;
export type UpdateWorkspaceMutationError = ErrorType<ApiErrorDto>;
export type UpdateWorkspaceMutationVariables = {
  id: string;
  data: BodyType<UpdateWorkspaceDto>;
};

/**
 * @summary Update a workspace: name, website, onboarding answers and its General settings.
 */
export const useUpdateWorkspace = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateWorkspace>>,
      TError,
      UpdateWorkspaceMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateWorkspace>>,
  TError,
  UpdateWorkspaceMutationVariables,
  TContext
> => {
  return useMutation(getUpdateWorkspaceMutationOptions(options), queryClient);
};
/**
 * @summary Delete a workspace. Owner only.
 */
export const deleteWorkspace = (id: string, signal?: AbortSignal) => {
  return customInstance<void>({
    url: `/api/v1/workspaces/${id}`,
    method: 'DELETE',
    signal,
  });
};

export const getDeleteWorkspaceMutationKey = () => ['deleteWorkspace'] as const;

export const getDeleteWorkspaceMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteWorkspace>>,
    TError,
    DeleteWorkspaceMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteWorkspace>>,
  TError,
  DeleteWorkspaceMutationVariables,
  TContext
> => {
  const mutationKey = getDeleteWorkspaceMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteWorkspace>>,
    DeleteWorkspaceMutationVariables
  > = (props) => {
    const { id } = props ?? {};

    return deleteWorkspace(id);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteWorkspaceMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteWorkspace>>
>;

export type DeleteWorkspaceMutationError = ErrorType<ApiErrorDto>;
export type DeleteWorkspaceMutationVariables = { id: string };

/**
 * @summary Delete a workspace. Owner only.
 */
export const useDeleteWorkspace = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteWorkspace>>,
      TError,
      DeleteWorkspaceMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteWorkspace>>,
  TError,
  DeleteWorkspaceMutationVariables,
  TContext
> => {
  return useMutation(getDeleteWorkspaceMutationOptions(options), queryClient);
};
/**
 * @summary Start a logo upload: returns a short-lived URL to PUT the image to
(PNG, JPG, WebP or SVG, up to 2 MB), then call setLogo with the key.
 */
export const createLogoUpload = (
  id: string,
  createImageUploadDto: BodyType<CreateImageUploadDto>,
  signal?: AbortSignal,
) => {
  return customInstance<ImageUploadDto>({
    url: `/api/v1/workspaces/${id}/logo/upload`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createImageUploadDto,
    signal,
  });
};

export const getCreateLogoUploadMutationKey = () => ['createLogoUpload'] as const;

export const getCreateLogoUploadMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createLogoUpload>>,
    TError,
    CreateLogoUploadMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createLogoUpload>>,
  TError,
  CreateLogoUploadMutationVariables,
  TContext
> => {
  const mutationKey = getCreateLogoUploadMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createLogoUpload>>,
    CreateLogoUploadMutationVariables
  > = (props) => {
    const { id, data } = props ?? {};

    return createLogoUpload(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateLogoUploadMutationResult = NonNullable<
  Awaited<ReturnType<typeof createLogoUpload>>
>;
export type CreateLogoUploadMutationBody = BodyType<CreateImageUploadDto>;
export type CreateLogoUploadMutationError = ErrorType<ApiErrorDto>;
export type CreateLogoUploadMutationVariables = {
  id: string;
  data: BodyType<CreateImageUploadDto>;
};

/**
 * @summary Start a logo upload: returns a short-lived URL to PUT the image to
(PNG, JPG, WebP or SVG, up to 2 MB), then call setLogo with the key.
 */
export const useCreateLogoUpload = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createLogoUpload>>,
      TError,
      CreateLogoUploadMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createLogoUpload>>,
  TError,
  CreateLogoUploadMutationVariables,
  TContext
> => {
  return useMutation(getCreateLogoUploadMutationOptions(options), queryClient);
};
/**
 * @summary Use an uploaded image as the workspace logo, replacing the previous one.
 */
export const setLogo = (
  id: string,
  setImageDto: BodyType<SetImageDto>,
  signal?: AbortSignal,
) => {
  return customInstance<WorkspaceDto>({
    url: `/api/v1/workspaces/${id}/logo`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: setImageDto,
    signal,
  });
};

export const getSetLogoMutationKey = () => ['setLogo'] as const;

export const getSetLogoMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof setLogo>>,
    TError,
    SetLogoMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof setLogo>>,
  TError,
  SetLogoMutationVariables,
  TContext
> => {
  const mutationKey = getSetLogoMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof setLogo>>,
    SetLogoMutationVariables
  > = (props) => {
    const { id, data } = props ?? {};

    return setLogo(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type SetLogoMutationResult = NonNullable<Awaited<ReturnType<typeof setLogo>>>;
export type SetLogoMutationBody = BodyType<SetImageDto>;
export type SetLogoMutationError = ErrorType<ApiErrorDto>;
export type SetLogoMutationVariables = { id: string; data: BodyType<SetImageDto> };

/**
 * @summary Use an uploaded image as the workspace logo, replacing the previous one.
 */
export const useSetLogo = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof setLogo>>,
      TError,
      SetLogoMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof setLogo>>,
  TError,
  SetLogoMutationVariables,
  TContext
> => {
  return useMutation(getSetLogoMutationOptions(options), queryClient);
};
/**
 * @summary Remove the workspace logo.
 */
export const removeLogo = (id: string, signal?: AbortSignal) => {
  return customInstance<WorkspaceDto>({
    url: `/api/v1/workspaces/${id}/logo`,
    method: 'DELETE',
    signal,
  });
};

export const getRemoveLogoMutationKey = () => ['removeLogo'] as const;

export const getRemoveLogoMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof removeLogo>>,
    TError,
    RemoveLogoMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof removeLogo>>,
  TError,
  RemoveLogoMutationVariables,
  TContext
> => {
  const mutationKey = getRemoveLogoMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof removeLogo>>,
    RemoveLogoMutationVariables
  > = (props) => {
    const { id } = props ?? {};

    return removeLogo(id);
  };

  return { mutationFn, ...mutationOptions };
};

export type RemoveLogoMutationResult = NonNullable<
  Awaited<ReturnType<typeof removeLogo>>
>;

export type RemoveLogoMutationError = ErrorType<ApiErrorDto>;
export type RemoveLogoMutationVariables = { id: string };

/**
 * @summary Remove the workspace logo.
 */
export const useRemoveLogo = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof removeLogo>>,
      TError,
      RemoveLogoMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof removeLogo>>,
  TError,
  RemoveLogoMutationVariables,
  TContext
> => {
  return useMutation(getRemoveLogoMutationOptions(options), queryClient);
};
